import { useState, useEffect } from 'react'
import { locationAPI } from '../services/api'
import { createLogger } from '../utils/logger'

const logger = createLogger('useSpotEntitySearch')

/**
 * useSpotEntitySearch - Server-side entity search for SpotMode
 *
 * Debounces the query (300ms) and fetches matching entities from the server.
 * Returns empty results for queries shorter than 2 characters.
 *
 * @param {Object} params
 * @param {string} params.query - Search query string
 * @returns {{ results: Array, isSearching: boolean }}
 *
 * @atomic - Single responsibility: debounced entity search
 * @idempotent - Same query always produces same fetch call
 */
export function useSpotEntitySearch({ query }) {
  const [results, setResults] = useState([])
  const [isSearching, setIsSearching] = useState(false)

  useEffect(() => {
    const trimmed = query?.trim() || ''

    if (trimmed.length < 2) {
      setResults([])
      setIsSearching(false)
      return
    }

    setIsSearching(true)

    const timer = setTimeout(async () => {
      try {
        const response = await locationAPI.searchEntities({ query: trimmed, limit: 20 })
        setResults(response.data.entities || [])
      } catch (err) {
        logger.error('Entity search failed', err)
        setResults([])
      } finally {
        setIsSearching(false)
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [query])

  return { results, isSearching }
}
